import {
  Box,
  Icon,
  Stack,
  Typography,
} from '@ace-clublabs/shared-component-library';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ContactInfoForm, {
  type ContactInfoFields,
} from '@/components/shared/ContactInfoForm';
import ErrorPage from '@/components/shared/ErrorPage';
import LoadingContainer from '@/components/shared/LoadingContainer';
import ProfileImage from '@/components/shared/ProfileImage';
import SplitPageLayout from '@/components/shared/SplitPageLayout';
import { useAppointmentCreation } from '@/features/confirmation/api/useAppointmentCreation';
import useLandingPageRedirection from '@/hooks/useLandingPageRedirection';
import { Page } from '@/lib/tagging';
import useTagging from '@/lib/tagging/useTagging';
import useAppointmentStore, {
  isInBranchAppointment as isInBranchAppointmentSelected,
  isOnlineAppointment as isOnlineAppointmentSelected,
} from '@/store/AppointmentStore';
import useSessionStore from '@/store/SessionStore';
import useThemeStore from '@/store/ThemeStore';
import AgentInfoContactInfoPage from '../components/AgentInfoContactInfoPage';
import type { ContactInfo } from '../utils/ContactInfo';

const ContactInfoLuxury = () => {
  const [isCreatingAppointment, setIsCreatingAppointment] = useState(false);
  const [isError, setIsError] = useState(false);
  const hasTagged = useRef(false);

  const {
    selectedDate,
    selectedStartTime,
    selectedEndTime,
    selectedAgent,
    selectedDestination,
    searchedDestination,
    contactInfo,
    setContactInfo,
  } = useAppointmentStore();

  const { sessionSource, sessionId, flowType } = useSessionStore();

  const { isLuxuryTheme } = useThemeStore();

  const isInBranchAppointment = useAppointmentStore(
    isInBranchAppointmentSelected,
  );
  const isOnlineAppointment = useAppointmentStore(isOnlineAppointmentSelected);

  const tagging = useTagging();

  const redirectPage = useLandingPageRedirection();

  const navigate = useNavigate();

  const regularFields = useMemo(() => {
    const fields: ContactInfoFields[] = [
      'firstName',
      'lastName',
      'zipCode',
      'communicationPreference',
      'phoneNumber',
      'email',
      'expectedTravelTime',
      'typeOfTrip',
      'additionalInformation',
    ];

    return isInBranchAppointment
      ? fields.filter((field) => field !== 'communicationPreference')
      : fields;
  }, [isInBranchAppointment]);

  const luxuryFields: ContactInfoFields[] = useMemo(
    () => [
      'firstName',
      'lastName',
      'zipCode',
      'communicationPreference',
      'phoneNumber',
      'email',
      'additionalInformation',
    ],
    [],
  );

  useEffect(() => {
    if (
      isInBranchAppointment &&
      contactInfo.communicationPreference !== 'In Person'
    ) {
      const updatedContactInfo: ContactInfo = {
        ...contactInfo,
        communicationPreference: 'In Person',
      };
      setContactInfo(updatedContactInfo);
    }
  }, [isInBranchAppointment, contactInfo, setContactInfo]);

  const handleIsCreatingAppointment = useCallback(() => {
    setIsCreatingAppointment(true);
  }, []);

  const handleError = useCallback(() => {
    setIsError(true);
    setIsCreatingAppointment(false);
  }, []);

  useEffect(() => {
    if (!hasTagged.current) {
      const trvEndLocation = searchedDestination || selectedDestination.name;

      const taggingData = {
        page: Page.contactInfo,
        sessionid: sessionId,
        flow: flowType,
        trv_end_location: trvEndLocation,
        comm_pref: isOnlineAppointment
          ? 'phone'
          : isInBranchAppointment
            ? 'in person'
            : undefined,
      };

      tagging.tag('view', sessionSource, taggingData);

      hasTagged.current = true;
    }
  }, [
    tagging.tag,
    sessionSource,
    sessionId,
    flowType,
    searchedDestination,
    selectedDestination.name,
    isOnlineAppointment,
    isInBranchAppointment,
  ]);

  const {
    handleStartAppointmentCreation,
    isProcessCustomerAccountSuccess,
    isCreateAppointmentSuccess,
  } = useAppointmentCreation({
    onStart: handleIsCreatingAppointment,
    onProcessCustomerAccountError: handleError,
    onCreateAppointmentError: handleError,
  });

  if (isError) {
    return <ErrorPage onClickTryAgain={() => navigate(redirectPage)} />;
  }

  if (isCreatingAppointment) {
    const radiosInfo = [
      {
        radioMessage: 'Gathering your information',
        isChecked: isProcessCustomerAccountSuccess,
      },
      {
        radioMessage: 'Submitting your appointment',
        isChecked: isCreateAppointmentSuccess,
      },
    ];

    return (
      <LoadingContainer
        message='It may take up to 30 seconds to schedule your appointment...'
        radiosInfo={radiosInfo}
      />
    );
  }

  return (
    <SplitPageLayout
      leftSide={
        <Stack
          direction='column'
          spacing={4}
          sx={[
            {
              px: 2,
              justifyContent: 'center',
              height: '100%',
            },
            {
              mt: {
                xs: 4,
                lg: 0,
              },
            },
          ]}
        >
          <Stack
            direction={
              isLuxuryTheme
                ? 'column'
                : {
                    lg: 'column',
                    xs: 'row',
                  }
            }
            spacing={
              isLuxuryTheme
                ? 0
                : {
                    lg: 0,
                    xs: 1.25,
                  }
            }
            sx={{
              alignItems: 'center',
              textAlign: 'center',
            }}
          >
            <ProfileImage
              src={selectedAgent.image || ''}
              alt={selectedAgent.name || ''}
              size={isLuxuryTheme ? 'big' : 'responsive'}
            />
            <Stack
              direction='column'
              sx={{
                mt: 1.25,
              }}
            >
              <Typography
                variant='h4'
                sx={[
                  {
                    fontSize: '24px',
                    textAlign: {
                      xs: 'left',
                      lg: 'center',
                    },
                  },
                  isLuxuryTheme && {
                    fontSize: '27px',
                  },
                ]}
              >
                {selectedAgent.name || ''}
              </Typography>
              <Stack
                direction='row'
                spacing={1}
                justifyContent={isLuxuryTheme ? 'center' : { lg: 'center' }}
                alignItems='center'
              >
                <Icon
                  iconName={
                    isLuxuryTheme
                      ? 'location-dot-regular'
                      : 'location-dot-duotone'
                  }
                  color='primary'
                />
                <Typography
                  variant='body1'
                  sx={{
                    textAlign: 'left',
                  }}
                >
                  {selectedAgent.location || 'Online Only'}
                </Typography>
              </Stack>
            </Stack>
          </Stack>
          <AgentInfoContactInfoPage
            agentName={selectedAgent.name || ''}
            selectedDate={selectedDate}
            selectedStartTime={selectedStartTime}
            selectedEndTime={selectedEndTime}
            selectedDestination={selectedDestination}
          />
        </Stack>
      }
      rightSide={
        <Box
          sx={[
            {
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            },
          ]}
        >
          <ContactInfoForm
            formHeader={`How should ${selectedAgent.name?.split(' ')[0]} contact you?`}
            fields={isLuxuryTheme ? luxuryFields : regularFields}
            formValues={contactInfo}
            updateField={setContactInfo}
            onSubmit={handleStartAppointmentCreation}
          />
        </Box>
      }
    />
  );
};

export default ContactInfoLuxury;
